import { ethers } from "ethers";
import {
  createContext,
  PropsWithChildren,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import ContractInfo from "../ContractInfo";
import CONTRACT_INFO from "../ContractInfo";

export enum Refs {
  home = "home",
  about = "about",
  vision = "vision",
  gms = "gms",
  team = "team",
  faq = "faq",
}

type TwitterUser = {
  twitterId: string;
  pfpSince: {
    _seconds: number;
    _nanoseconds: number;
  };
  twitterName: string;
  twitterUsername: string;
  bioSince: {
    _seconds: number;
    _nanoseconds: number;
  };
};

export type Context = {
  wallet?: string;
  walletName?: string;
  connect(): void;
  signature?: string;
  sign(): Promise<string>;
  twitterUser?: TwitterUser;
  getTwitterUser(): void;
  connectTwitter(): void;
  refs?: {
    [key in Refs]: RefObject<HTMLDivElement>;
  };
  userImages: string[];
  saveUserImages(images: string[]): void;
};

const connectWallet = async (): Promise<string | undefined> => {
  const isETHWalletAvailable = !!window.ethereum;
  if (!isETHWalletAvailable) {
    return undefined;
  }
  const accounts = await window.ethereum.request({
    method: "eth_requestAccounts",
  });
  return accounts[0];
};

type StoredUser = {
  signature?: string;
  twitterUser?: TwitterUser;
};

export const getStoredUser = (wallet?: string): StoredUser => {
  if (!wallet) {
    return {};
  }
  try {
    const existingUser = JSON.parse(
      window.localStorage.getItem(wallet || "") || "{}"
    );
    if (existingUser && typeof existingUser === "object") {
      return existingUser;
    }
  } catch (e) {
    return {};
  }
  return {};
};

const storeSignature = (wallet?: string, signature?: string): void => {
  const existingUser = getStoredUser(wallet);
  window.localStorage.setItem(
    wallet || "",
    JSON.stringify({
      ...existingUser,
      signature,
    })
  );
};

const storeTwitterUser = (
  wallet: string | undefined,
  twitterUser: TwitterUser
): void => {
  const existingUser = getStoredUser(wallet);
  window.localStorage.setItem(
    wallet || "",
    JSON.stringify({
      ...existingUser,
      twitterUser,
    })
  );
};

export const initialContext: Context = {
  wallet: undefined,
  walletName: undefined,
  signature: undefined,
  sign: () => Promise.resolve(""),
  connect: connectWallet,
  getTwitterUser: () => {},
  connectTwitter: () => {},
  twitterUser: undefined,
  userImages: [],
  saveUserImages() {},
};

const isHomestead = false; // window.chain === 'homestead'

export const getENS = async (
  wallet: string | undefined
): Promise<string | undefined> => {
  if (!wallet || !isHomestead) {
    return undefined;
  }
  return (
    (await new ethers.providers.Web3Provider(
      window.ethereum,
      CONTRACT_INFO[window.chain].chainId
    ).lookupAddress(wallet)) || undefined
  );
};

export const AppContext = createContext<Context>(initialContext);

const message =
  "Welcome to Reply Guys! By signing this message you agree to spreading only good vibes!";

export const AppContextProvider = (props: PropsWithChildren) => {
  const [wallet, setWallet] = useState<string>();
  const [userImages, saveUserImages] = useState<string[]>([]);
  const [signature, setSignature] = useState(getStoredUser(wallet).signature);
  const [twitterUser, setTwitterUser] = useState(
    getStoredUser(wallet).twitterUser
  );
  const [walletName, setWalletName] = useState<string>();
  const homeRef = useRef<HTMLDivElement>() as RefObject<HTMLDivElement>;
  const aboutRef = useRef<HTMLDivElement>() as RefObject<HTMLDivElement>;
  const visionRef = useRef<HTMLDivElement>() as RefObject<HTMLDivElement>;
  const gmsRef = useRef<HTMLDivElement>() as RefObject<HTMLDivElement>;
  const teamRef = useRef<HTMLDivElement>() as RefObject<HTMLDivElement>;
  const faqRef = useRef<HTMLDivElement>() as RefObject<HTMLDivElement>;

  useEffect(() => {
    setSignature(getStoredUser(wallet).signature);
    setTwitterUser(getStoredUser(wallet).twitterUser);
  }, [wallet]); // eslint-disable-line react-hooks/exhaustive-deps

  const sign = async (): Promise<string> => {
    const newSignature = await new ethers.providers.Web3Provider(
      window.ethereum,
      CONTRACT_INFO[window.chain].chainId
    )
      .getSigner()
      .signMessage(message);
    storeSignature(wallet, newSignature);
    setSignature(newSignature);
    return newSignature;
  };
  const connect = async () => {
    const wallet = await connectWallet();
    setWalletName(await getENS(wallet));
    setWallet(wallet);
  };

  const getTwitterUser = async (newSignature?: string) => {
    if (newSignature || signature) {
      const response = await window.fetch(
        `${ContractInfo[window.chain].backendUrl}/users?signature=${
          newSignature || signature
        }`
      );
      if (response.ok) {
        const user = await response.json();
        storeTwitterUser(wallet, {
          ...user,
        });
        setTwitterUser(user);
        if (user.images?.length > 0) {
          saveUserImages(user.images);
        }
      }
    }
  };

  const connectTwitter = async () => {
    const response = await window.fetch(
      `${ContractInfo[window.chain].backendUrl}/twitter-auth`
    );
    const twitterUrl = await response.text();
    window.location.assign(twitterUrl);
  };

  useEffect(() => {
    // connect();
    window.ethereum?.on(
      "accountsChanged",
      async ([wallet0]: (string | undefined)[]) => {
        if (wallet0 && isHomestead) {
          const name = await new ethers.providers.Web3Provider(
            window.ethereum,
            CONTRACT_INFO[window.chain].chainId
          ).lookupAddress(wallet0);
          setWalletName(name || undefined);
        }
        setWallet(wallet0);
      }
    );
  }, []);

  useEffect(() => {
    getTwitterUser(signature);
  }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppContext.Provider
      value={{
        connect,
        wallet,
        walletName,
        sign,
        signature,
        getTwitterUser,
        connectTwitter,
        twitterUser,
        userImages,
        saveUserImages,
        refs: {
          home: homeRef,
          about: aboutRef,
          team: teamRef,
          vision: visionRef,
          gms: gmsRef,
          faq: faqRef,
        },
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
};
